import React, { useEffect, useState } from 'react';

import { ContentDeliverable, DeliverableType, License, PendingLicenseRestriction, SelectedContent, SelectedContentReference, Transaction, TransactionApi } from 'apis/TransactionApi';
import { LicenseRestrictionGMetrixTable } from 'pages/access_codes/components/LicenseRestrictionGMetrixTable/LicenseRestrictionGMetrixTable';
import type { ContentTiedToExpiredOrder } from 'pages/access_codes/components/TransactionProfile/TransactionProfile';
import { GMetrixAccordion, GMetrixAccordionItem } from 'components/GMetrixAccordion/GMetrixAccordion';
import { getLowestLicenseAmount, handlePendingLicenseRestrictions } from 'utils/objects';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { StudentGroupWithDetails } from 'apis/StudentGroupApi';
import { TransactionData } from 'pages/access_codes/context';
import { Tab, TabsContext } from 'components/Tabs/Tabs';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { Modal } from 'components/modal_manager';
import { GroupData } from 'pages/groups/context';
import { Alert, Badge } from 'react-bootstrap';
import { useContext } from 'components/Tabs';

import 'components/TransactionOrderExpiredDialog/transaction-order-expired-dialog.scss';

type TransactionOrderExpiredDialogProps = {
	context: 'Classroom' | 'Access Code';
	contextAccessCode: React.Context<TabsContext<TransactionData>>;
	contextClassroom: React.Context<TabsContext<GroupData>>;
	inactiveOrdersWContentOnOpenTransaction: ContentTiedToExpiredOrder[];
	reloadAccessCode?:(dataItem: Transaction, newTransaction?: boolean, overrideSubTab?: number) => Promise<void>;
	reloadClassroom?: (dataItemGroup: StudentGroupWithDetails, newGroup?: boolean) => Promise<void>;
	setShow: (value: React.SetStateAction<boolean>) => void;
	show: boolean;
};

export const TransactionOrderExpiredDialog = ({
    context,
    contextAccessCode,
    contextClassroom,
    inactiveOrdersWContentOnOpenTransaction,
    reloadAccessCode,
    reloadClassroom,
    setShow,
    show
}: TransactionOrderExpiredDialogProps) => {

    const { formatMessage } = useIntl();
    const { createToast } = useNotification();
    const { pageContext, update: updateContext } = useContext(
        (context === 'Access Code' ? contextAccessCode : contextClassroom) as React.Context<TabsContext<TransactionData & GroupData>>
    );

    const [selectedPendingLicenseRestrictions, setSelectedPendingLicenseRestrictions] = useState<License[][]>([]);
    const [pendingLicenseRestrictions, setPendingLicenseRestrictions] = useState<PendingLicenseRestriction[]>([]);
    const [flatListAllContent, setFlatListAllContent] = useState<ContentDeliverable[]>([]);
    const [runOnce, setRunOnce] = useState(true);
    const [runOnce2, setRunOnce2] = useState(true);
    const [activeTransferOrdersPending, setActiveTransferOrdersPending] = useState(true);
    const [disabledActionButton, setDisabledActionButton] = useState(false);
    const [noOrdersToChangeTo, setNoOrdersToChangeTo] = useState(false);

    useEffect(() => {

        if (pendingLicenseRestrictions.length > 0) {

            setActiveTransferOrdersPending(false);

        }

    }, [pendingLicenseRestrictions, selectedPendingLicenseRestrictions]);

    useEffect(() => {

        // Would've used TestContent on selected tab but it's tests only.
        if (flatListAllContent.length === 0 && runOnce && pageContext?.Data.transactionDetails) {

            TransactionApi.GetAllContentForTransactions(false, true, pageContext.Data.transactionDetails.PartnerId ?? null)
                .then(response => {

                    setFlatListAllContent(response.contentFlatList);

                })
                .catch(console.error);
            setRunOnce(false);

        }
        if (inactiveOrdersWContentOnOpenTransaction.length > 0 && flatListAllContent.length > 0) {

            generateLicenseRestrictionTable();

        } else if (flatListAllContent.length === 0 && !runOnce) {

            setRunOnce2(false);
            if (!runOnce2) {

                setNoOrdersToChangeTo(true);

            }

        } else {

            setSelectedPendingLicenseRestrictions([]);
            setPendingLicenseRestrictions([]);

        }

    }, [inactiveOrdersWContentOnOpenTransaction, flatListAllContent]);

    const generateLicenseRestrictionTable = async () => {

        // Assigned content only carries its (expired) restricting order, so resolve every order
        // that currently grants the content from the partner's full content list.
        const contentThatHasAnExpiredOrder = inactiveOrdersWContentOnOpenTransaction
            .map(inactiveOrderWContent => inactiveOrderWContent.content)
            .flat()
            .map(checkedPieceOfContent => {

                const getOrderIdsPieceOfContent = flatListAllContent.find(
                    pieceOfContent => pieceOfContent.Id === checkedPieceOfContent.Id &&
						pieceOfContent.DeliverableTypeId === checkedPieceOfContent.DeliverableTypeId &&
						!!pieceOfContent.IsCustomTest === !!checkedPieceOfContent.IsCustomTest
                );
                return getOrderIdsPieceOfContent
                    ? { ...checkedPieceOfContent, OrderIds: getOrderIdsPieceOfContent.OrderIds }
                    : undefined;

            })
            .filter((pieceOfContent): pieceOfContent is ContentDeliverable => pieceOfContent !== undefined);
        const listOfPendingLicenseRestrictions: PendingLicenseRestriction[] = [];

        for (let i = 0; i < contentThatHasAnExpiredOrder.length; i++) {

            const selectedItem = contentThatHasAnExpiredOrder[i];
            const selectedItemReference: SelectedContent = {
                DeliverableTypeId: selectedItem.DeliverableTypeId,
                Id: selectedItem.Id,
                Name: selectedItem.Name,
                OrderIds: selectedItem.OrderIds
            };

            if (pageContext?.Data.licenses && context === 'Access Code') {

                handlePendingLicenseRestrictions(
                    selectedItemReference,
                    listOfPendingLicenseRestrictions,
                    pageContext?.Data.licenses
                );

            }

            if (pageContext?.Data.licenses && context === 'Classroom') {

                handlePendingLicenseRestrictions(
                    selectedItemReference,
                    listOfPendingLicenseRestrictions,
                    pageContext?.Data.licenses
                );

            }

        }
        if (listOfPendingLicenseRestrictions.length > 0) {

            setPendingLicenseRestrictions(listOfPendingLicenseRestrictions);
            setSelectedPendingLicenseRestrictions(listOfPendingLicenseRestrictions.map(() => []));

        } else {

            setNoOrdersToChangeTo(true);

        }

    };

    const reloadContext = () => {

        if (context === 'Access Code' && reloadAccessCode && pageContext?.Data.transactionDetails) {

            reloadAccessCode(pageContext.Data.transactionDetails, false);

        }
        if (context === 'Classroom' && reloadClassroom && pageContext?.Data.groupDetails) {

            reloadClassroom(pageContext.Data.groupDetails, false);

        }

    };

    const removeContentFromTransaction = async (
        currentContent: ContentDeliverable[],
        contentToDelete: ContentDeliverable[],
        pendingDeleteSave = true
    ) => {

        if (!pendingDeleteSave) {

            const testIds: number[] = [];
            const courseIds: number[] = [];
            const bookIds: number[] = [];
            contentToDelete.forEach(pieceOfContent => {

                switch (true) {

                    case pieceOfContent.DeliverableTypeId === DeliverableType.Test:
                        testIds.push(pieceOfContent.Id);
                        break;
                    case pieceOfContent.DeliverableTypeId === DeliverableType.Course:
                        courseIds.push(pieceOfContent.Id);
                        break;
                    case pieceOfContent.DeliverableTypeId === DeliverableType.Book:
                        bookIds.push(pieceOfContent.Id);
                        break;

                }

            });

            if (pageContext && pageContext.Data.transactionDetails && context === 'Access Code') {

                await TransactionApi.DeleteContentFromTransaction(
                    pageContext.Data.transactionDetails?.Id,
                    testIds,
                    bookIds,
                    courseIds
                );

            }

            if (pageContext && pageContext.Data.transactionDetails && context === 'Classroom') {

                await TransactionApi.DeleteContentFromTransaction(
                    pageContext.Data.transactionDetails?.Id,
                    testIds,
                    bookIds,
                    courseIds
                );

            }

        } else {

            const changedCurrentContent: ContentDeliverable[] = [...currentContent];
            contentToDelete.forEach(expiredContent => {

                const findIndexOfCurrentContent = currentContent.findIndex(pieceOfCurrentContent => {

                    if (
                        pieceOfCurrentContent.Id === expiredContent.Id &&
						pieceOfCurrentContent.DeliverableTypeId === expiredContent.DeliverableTypeId
                    ) {

                        if (expiredContent.IsCustomTest) {

                            if (expiredContent.IsCustomTest === pieceOfCurrentContent.IsCustomTest) {

                                return pieceOfCurrentContent;

                            }

                        } else {

                            return pieceOfCurrentContent;

                        }

                    }

                });
                if (findIndexOfCurrentContent !== -1) {

                    changedCurrentContent[findIndexOfCurrentContent].PendingDelete = true;

                }

            });

            if (pageContext && context === 'Access Code') {

                updateContext({
                    content: changedCurrentContent
                });

            }
            if (pageContext && context === 'Classroom') {

                updateContext({
                    content: changedCurrentContent
                });

            }

        }

    };

    const transferContentToActiveOrders = async () => {

        if (selectedPendingLicenseRestrictions.length !== pendingLicenseRestrictions.length) {

            setPendingLicenseRestrictions(pendingLicenseRestrictions);
            setSelectedPendingLicenseRestrictions(pendingLicenseRestrictions.map(() => []));
            return false;

        }
        const validPendingLicenseRestrictions = [...pendingLicenseRestrictions];
        let validPending = true;
        if (validPendingLicenseRestrictions.length > 0) {

            for (let i = 0; i < selectedPendingLicenseRestrictions.length; i++) {

                if (selectedPendingLicenseRestrictions[i].length === 0) {

                    validPending = false;
                    validPendingLicenseRestrictions[i].Valid = false;

                } else {

                    validPendingLicenseRestrictions[i].Valid = true;

                }

            }

        }
        if (!validPending) {

            setPendingLicenseRestrictions(validPendingLicenseRestrictions);
            return false;

        }

        if (pageContext) {

            await separateDeliverablesByContext(pageContext, validPendingLicenseRestrictions);

        }

        return true;

    };

    const separateDeliverablesByContext = async (
        typeOfContext: Tab<TransactionData> | Tab<GroupData>,
        validPendingLicenseRestrictions: PendingLicenseRestriction[]
    ) => {

        if (typeOfContext?.Data.transactionDetails?.OrderIds) {

            let orderIdsToAdd = selectedPendingLicenseRestrictions
                .flat()
                .map(licenseRestriction => licenseRestriction.OrderId);
            const pendingTests: SelectedContentReference[] = [];
            const pendingBooks: SelectedContentReference[] = [];
            const pendingCourses: SelectedContentReference[] = [];

            insertPendedLicenseRestrictionDeliverables(
                orderIdsToAdd,
                validPendingLicenseRestrictions,
                pendingTests,
                pendingBooks,
                pendingCourses
            );
            orderIdsToAdd = [...orderIdsToAdd, ...typeOfContext.Data.transactionDetails.OrderIds];
            orderIdsToAdd = orderIdsToAdd.filter(
                orderId => !inactiveOrdersWContentOnOpenTransaction.find(
                    expiredOrder => expiredOrder.expiredOrder.OrderId === orderId
                )
            );

            await TransactionApi.Update({
                ...typeOfContext.Data.transactionDetails,
                OrderIds: orderIdsToAdd
            });

            await TransactionApi.AddContentToTransaction(
                typeOfContext.Data.transactionDetails.Id,
                pendingTests,
                pendingBooks,
                pendingCourses
            );

            reloadContext();

        }

    };

    const insertPendedLicenseRestrictionDeliverables = (
        orderIdsToAdd: number[],
        validPendingLicenseRestrictions: PendingLicenseRestriction[],
        pendingTests: SelectedContentReference[],
        pendingBooks: SelectedContentReference[],
        pendingCourses: SelectedContentReference[]
    ) => {

        validPendingLicenseRestrictions.map((validPendingLicenseRestriction, i) => {

            validPendingLicenseRestriction.ConflictingDeliverableList.map(contentLevelObj => {

                switch (true) {

                    case contentLevelObj.DeliverableTypeId === DeliverableType.Test:
                        pendingTests.push({ id: contentLevelObj.Id, restrictedOrderId: orderIdsToAdd[i] });
                        break;
                    case contentLevelObj.DeliverableTypeId === DeliverableType.Book:
                        pendingBooks.push({ id: contentLevelObj.Id, restrictedOrderId: orderIdsToAdd[i] });
                        break;
                    case contentLevelObj.DeliverableTypeId === DeliverableType.Course:
                        pendingCourses.push({ id: contentLevelObj.Id, restrictedOrderId: orderIdsToAdd[i] });
                        break;

                }

            });

        });

    };

    const onTransferContentClick = async () => {

        setDisabledActionButton(true);
        try {

            const success = await transferContentToActiveOrders();
            if (!success) {

                setDisabledActionButton(false);
                return;

            }
            createToast(formatMessage({ id: 'content_transferred_successfully.6bd5f945', defaultMessage: 'Content transferred successfully.' }), 'success');
            setShow(false);

        } catch (e) {

            console.error(e);
            setDisabledActionButton(false);
            createToast(formatMessage({ id: 'failed_to_transfer_content_please_try_ag.c2f8c7ea', defaultMessage: 'Failed to transfer content. Please try again.' }), 'danger');

        }

    };

    const onRemoveContentClick = async () => {

        if (!pageContext?.Data.content) {

            return;

        }
        setDisabledActionButton(true);
        try {

            await removeContentFromTransaction(
                pageContext.Data.content,
                inactiveOrdersWContentOnOpenTransaction
                    .map(inactiveOrderWContent => inactiveOrderWContent.content)
                    .flat(),
                false
            );
            reloadContext();
            createToast(formatMessage({ id: 'content_removed_successfully.c7b38eba', defaultMessage: 'Content removed successfully.' }), 'success');
            setShow(false);

        } catch (e) {

            console.error(e);
            setDisabledActionButton(false);
            createToast(formatMessage({ id: 'failed_to_remove_content_please_try_agai.18250ed4', defaultMessage: 'Failed to remove content. Please try again.' }), 'danger');

        }

    };

    // A transferable license is only usable when we found active orders to move uses to AND those
    // orders actually surfaced selectable licenses. If the license options fail to populate (empty
    // tables), fall back to the "no transfer available" state so only Remove Content is offered.
    const hasTransferableLicenses = pendingLicenseRestrictions.some(
        licenseRestriction => licenseRestriction.LicenseRestrictions.length > 0
    );
    const canTransfer = !noOrdersToChangeTo && hasTransferableLicenses;

    // The transfer button stays disabled until the user picks a license to draw uses from.
    const hasLicenseSelection = selectedPendingLicenseRestrictions.some(licenseArr => licenseArr.length > 0);
    const isClassroom = context === 'Classroom';

    return (
        <Modal onHide={() => setShow(false)} show={show} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>
                    <FormattedMessage defaultMessage="Resolve License Issue" id="resolve_license_issue.393fd474" />
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="transaction-order-expired-dialog">

                <Alert className="d-flex" variant="warning">
                    <i className="fa-regular fa-triangle-exclamation me-3 mt-1" />
                    <div>
                        <strong className="d-block mb-1">
                            {isClassroom
                                ? <FormattedMessage defaultMessage="Some content on this Classroom is no longer covered" id="some_content_on_this_classroom_is_no_lon.791a459b" />
                                : <FormattedMessage defaultMessage="Some content on this Access Code is no longer covered" id="some_content_on_this_access_code_is_no_l.cd13463f" />}
                        </strong>
                        <span>
                            {isClassroom
                                ? <FormattedMessage defaultMessage={'Some of the content assigned to this Classroom is linked to the following Order(s), which is\r\n						either expired or has no available uses remaining.'} id="some_of_the_content_assigned_to_this_cla.3a90beee" />
                                : <FormattedMessage defaultMessage={'Some of the content assigned to this Access Code is linked to the following Order(s), which is\r\n						either expired or has no available uses remaining.'} id="some_of_the_content_assigned_to_this_acc.50411325" />}
                            {' '}
                            <FormattedMessage defaultMessage="Choose how you'd like to handle the affected content below." id="choose_how_youd_like_to_handle_the_affec.b3b6adce" />
                        </span>
                    </div>
                </Alert>

                <div className="d-flex align-items-center mt-4 mb-2">
                    <Badge pill bg="primary" className="rounded-circle">
                        1
                    </Badge>
                    <strong className="ms-2">
                        <FormattedMessage defaultMessage="Impacted order" id="impacted_order.c23272e3" />
                    </strong>
                    <span className="text-muted ms-2">
                        —
                        {' '}
                        <FormattedMessage defaultMessage="content linked to this Order is affected" id="content_linked_to_this_order_is_affected.aba88cda" />
                    </span>
                </div>

                <div className="transaction-order-expired-dialog__content-info">
                    {inactiveOrdersWContentOnOpenTransaction.map((inactiveOrderWContent, i) => (
                        <GMetrixAccordion className={`border rounded overflow-hidden ${i === 0 ? '' : 'mt-2'}`} key={`${inactiveOrderWContent.expiredOrder.Id}-${inactiveOrderWContent.expiredOrder.LicenseName}-${i}`}>
                            <GMetrixAccordionItem
                                title={(
                                    <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1 me-3">
                                        <b className="mx-2">
                                            {inactiveOrderWContent.expiredOrder.LicenseName}
                                        </b>
                                        <Badge bg="danger" className="text-uppercase me-auto">
                                            {inactiveOrderWContent.expiredOrder.OrderExpireDate
                                                && new Date(inactiveOrderWContent.expiredOrder.OrderExpireDate) < new Date()
                                                ? <FormattedMessage defaultMessage="Expired" id="expired.3a3fc3a2" />
                                                : <FormattedMessage defaultMessage="No Uses Remaining" id="no_uses_remaining.e2057c00" />}
                                        </Badge>
                                        <span className="text-nowrap ms-2 text-danger fw-semibold">
                                            <FormattedMessage defaultMessage="Expires" id="expires.84d49534" />
                                            :
                                            {' '}
                                            {inactiveOrderWContent.expiredOrder.OrderExpireDateString}
                                        </span>
                                        <span className="text-muted text-nowrap">
                                            <FormattedMessage defaultMessage="Uses Remaining" id="uses_remaining.d26b9e27" />
                                            :
                                            {' '}
                                            {getLowestLicenseAmount(inactiveOrderWContent.expiredOrder)}
                                        </span>
                                    </div>
                                )}
                                expanded={i === 0}
                                headerClassName="header-override py-1"
                            >
                                <div className="transaction-order-expired-dialog__container">
                                    {inactiveOrderWContent.content.map((pieceOfContent, contentIndex) => (
                                        <div
                                            className={`ms-3 m-1 p-2 ${contentIndex === inactiveOrderWContent.content.length - 1
                                                ? ''
                                                : 'border-bottom'
                                            }`}
                                            key={`${pieceOfContent.Id}-${contentIndex}`}
                                        >
                                            {pieceOfContent.Name}
                                        </div>
                                    ))}
                                </div>
                            </GMetrixAccordionItem>
                        </GMetrixAccordion>
                    ))}
                </div>

                <div className="d-flex align-items-center mt-4 mb-2">
                    <Badge pill bg="primary" className="rounded-circle">
                        2
                    </Badge>
                    <strong className="ms-2">
                        <FormattedMessage defaultMessage="Choose how to resolve this" id="choose_how_to_resolve_this.84d09f39" />
                    </strong>
                    <span className="text-muted ms-2">
                        —
                        {' '}
                        {canTransfer
                            ? <FormattedMessage defaultMessage="do one of the two options below" id="do_one_of_the_two_options_below.26bf3290" />
                            : <FormattedMessage defaultMessage="transferring isn't available for this content" id="transferring_isnt_available_for_this_con.d6cc357b" />}
                    </span>
                </div>

                {canTransfer ? (
                    <>
                        <div className="border rounded p-3 mb-3">
                            <div className="d-flex align-items-center mb-2">
                                <strong>
                                    <FormattedMessage defaultMessage="Option 1 — Transfer to an active license" id="option_1_transfer_to_an_active_license.746fc1ba" />
                                </strong>
                                <Badge bg="info" className="ms-2">
                                    <FormattedMessage defaultMessage="Recommended" id="recommended.a071cd8f" />
                                </Badge>
                            </div>
                            <p className="text-muted">
                                <FormattedMessage defaultMessage="Keep this content available by moving its uses to another Order on your account that includes the same content. Select the license to draw uses from below, then click Transfer Content." id="keep_this_content_available_by_moving_it.e8ec0f7f" />
                            </p>
                            {!activeTransferOrdersPending ? (
                                pendingLicenseRestrictions.map((licenseRestriction, index) => (
                                    <div className="my-2" key={index + '-' + 'license-restriction'}>
                                        <LicenseRestrictionGMetrixTable
                                            conflictingDeliverableList={
                                                licenseRestriction.ConflictingDeliverableList
                                            }
                                            setSelectedPendingLicenseRestrictions={
                                                setSelectedPendingLicenseRestrictions
                                            }
                                            key={index}
                                            licenseRestrictions={licenseRestriction.LicenseRestrictions}
                                            nonAvailableLicenseList={licenseRestriction.NonAvailableLicenseList}
                                            parentSelectedPendingLicenseRestrictionIndex={index}
                                            selectedPendingLicenseRestrictions={selectedPendingLicenseRestrictions}
                                            valid={licenseRestriction.Valid}
                                        />
                                    </div>
                                ))
                            ) : (
                                <GMetrixLoader
                                    className="transaction-order-expired-dialog__loader"
                                />
                            )}
                        </div>

                        <div className="d-flex align-items-center text-muted my-2">
                            <hr className="flex-grow-1" />
                            <span className="mx-2 small">
                                <FormattedMessage defaultMessage="OR" id="or.80e18fc0" />
                            </span>
                            <hr className="flex-grow-1" />
                        </div>

                        <div className="border rounded p-3">
                            <strong className="d-block mb-1">
                                <FormattedMessage defaultMessage="Option 2 — Remove the content" id="option_2_remove_the_content.f81ead82" />
                            </strong>
                            <span className="text-muted">
                                <FormattedMessage defaultMessage="Don't need this content anymore? Click Remove Content to remove the affected content. This gives up access to it — nothing is transferred, and no active license is required." id="dont_need_this_content_anymore_click_rem.1e690ac2" />
                            </span>
                        </div>

                        <Alert className="border d-flex mt-3 mb-0" variant="light">
                            <i className="fa-regular fa-circle-info me-3 mt-1" />
                            <span>
                                <FormattedMessage defaultMessage="Either option lets you keep assigning students. Transferring keeps the affected content available; removing it does not." id="either_option_lets_you_keep_assigning_st.3a8cdf30" />
                            </span>
                        </Alert>
                    </>
                ) : (
                    <Alert className="border d-flex mb-0" variant="light">
                        <i className="fa-regular fa-circle-exclamation me-3 mt-1" />
                        <div>
                            <strong className="d-block mb-2">
                                <FormattedMessage defaultMessage="The affected content can't be transferred — there are no active Orders on your account that include it." id="the_affected_content_cant_be_transferred.584a1327" />
                            </strong>
                            <div>
                                <FormattedMessage defaultMessage="You have two options:" id="you_have_two_options.13c80a0d" />
                            </div>
                            <div className="mt-1">
                                <strong>
                                    <FormattedMessage defaultMessage="Keep the content" id="keep_the_content.588c3aed" />
                                </strong>
                                {' — '}
                                <FormattedMessage defaultMessage="contact your supplier to place a new order that includes it." id="contact_your_supplier_to_place_a_new_ord.950b899d" />
                            </div>
                            <div>
                                <strong>
                                    <FormattedMessage defaultMessage="Remove the content" id="remove_the_content.837877b3" />
                                </strong>
                                {' — '}
                                <FormattedMessage defaultMessage="if you no longer need it, click Remove Content below to remove it." id="if_you_no_longer_need_it_click_remove_co.c73a3b63" />
                            </div>
                        </div>
                    </Alert>
                )}

            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <GMetrixButton
                    disabled={disabledActionButton}
                    onClick={() => setShow(false)}
                    variant="outline-secondary"
                >
                    <FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" />
                </GMetrixButton>
                <div className="d-flex gap-2">
                    <GMetrixButton
                        disabled={disabledActionButton}
                        iconClass="fa-regular fa-trash"
                        onClick={onRemoveContentClick}
                        variant="outline-danger"
                    >
                        <FormattedMessage defaultMessage="Remove Content" id="remove_content.27546753" />
                    </GMetrixButton>
                    {canTransfer && (
                        <GMetrixButton
                            disabled={disabledActionButton || !hasLicenseSelection}
                            iconClass="fa-regular fa-arrow-right-arrow-left"
                            onClick={onTransferContentClick}
                            variant="primary"
                        >
                            <FormattedMessage defaultMessage="Transfer Content" id="transfer_content.1f220ba7" />
                        </GMetrixButton>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    );

};
